// What a widget sends back to the server, and what it must not.
//
// The server mints `source_sql` so a refresh can re-run the original query instead of
// regenerating a different one. It arrives with the result, and the client used to post
// it straight back on every save and every refresh. A request body carrying a literal
// SELECT is, to the gateway firewall, indistinguishable from SQL injection — so it is
// blocked, the refusal carries no CORS headers, and the browser reports only
// "Failed to fetch" while the request never reaches the application.
//
// Stripped for EVERY widget type, not just charts. `source_sql` and `source_tables` are
// declared fields on the shared chart-data schema, with descriptions the model reads, so
// the model fills them in for whatever it is asked to produce — the agent assigning them
// for four chart types is not the only way they get set. The save that was blocked on dev
// was a single-value KPI, and the firewall named `data.source_sql` as the match. Assuming
// a type cannot carry them is what made that failure look inexplicable; do not narrow this.
//
// Nothing in this frontend reads either field, so stripping them costs the UI nothing. The
// server keeps its own copy and carries it across writes that cannot include it.
//
// Pure and in its own module so it can be tested without mounting Insights, which pulls
// in the d3 renderers.

const SERVER_OWNED_DATA_FIELDS = ['source_sql', 'source_tables'];

export function withoutServerOnlyFields(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  // Return the same object when there is nothing to do: the caller also hands this to
  // React state, and a needless copy would break reference equality downstream.
  if (!SERVER_OWNED_DATA_FIELDS.some((f) => f in data)) return data;
  const copy = { ...data };
  SERVER_OWNED_DATA_FIELDS.forEach((f) => delete copy[f]);
  return copy;
}

// PRD-1578: what a text-only edit takes from the server's response. Only the words:
// the rest of the row is the server's copy, and its `last_computed_at` differs from the
// client-side stamp an in-session refresh leaves in state. The card reads a moved stamp
// as "an edit brought a new result" and discards its live data, so taking the whole
// response would reset a card for a rename.
export function withTextEdit(widget, updated) {
  return { ...widget, title: updated.title, display_label: updated.display_label ?? null };
}

// A KPI whose preview was refused is not saved. The server says why in the preview's own
// words, which is what the author needs to see in place of a generic failure.
export function savedRefusal(err) {
  return (err?.code === 'preview_refused' && err.message) || null;
}

export { SERVER_OWNED_DATA_FIELDS };
